import classNames from 'classnames';
import type { FormikErrors } from 'formik';
import moment from 'moment';
import React from 'react';
import { calculateExpirationTimestamp } from './verificationDateHelpers';
import * as styles from './VerificationFrequencyFields.module.scss';
import { Field } from '../components/Form';
import Select from '../components/Select';
import { VerificationUnit } from '../graphql/generated/graphql';

export type VerificationFormValues = {
  frequency: 'Monthly' | 'Quarterly' | 'Annually' | 'Custom';
  unit: VerificationUnit | null;
  quantity: number | null;
};

const UNITS = [
  { label: 'Weeks', value: VerificationUnit.Week },
  { label: 'Months', value: VerificationUnit.Month },
];

export const FREQUENCIES = [
  { frequency: 'Monthly', unit: VerificationUnit.Month, quantity: 1 },
  { frequency: 'Quarterly', unit: VerificationUnit.Month, quantity: 3 },
  { frequency: 'Annually', unit: VerificationUnit.Month, quantity: 12 },
  { frequency: 'Custom', unit: VerificationUnit.Week, quantity: 2 },
] as const;

export const validateVerificationFrequencyFields = ({
  quantity,
  unit,
}: VerificationFormValues) => {
  const errors: FormikErrors<VerificationFormValues> = {};
  const quantityNumber = Number(quantity);

  if (!Number.isInteger(quantityNumber) || quantityNumber <= 0) {
    errors.quantity = 'Invalid quantity';
  } else {
    const expirationTimestamp =
      unit && quantity
        ? calculateExpirationTimestamp(moment().valueOf(), {
            unit,
            quantity,
          }).getOrElse(() => null)
        : null;

    if (!expirationTimestamp) {
      errors.quantity = 'Invalid quantity';
    }
  }

  return errors;
};

interface VerificationFrequencyFieldsProps {
  frequency: VerificationFormValues['frequency'];
  setFieldValue: (field: string, value: unknown) => void;
}

const VerificationFrequencyFields = ({
  frequency,
  setFieldValue,
}: VerificationFrequencyFieldsProps) => (
  <>
    <label>
      <div className={styles.label}>Frequency of review</div>
      <Select
        options={FREQUENCIES.map(({ frequency: f }) => ({
          label: f,
          value: f,
        }))}
        blurInputOnSelect
        onChange={(newFrequency) =>
          Object.entries(
            FREQUENCIES.find(({ frequency: f }) => newFrequency === f) ?? {},
          ).map(([name, value]) => setFieldValue(name, value))
        }
        value={frequency}
      />
    </label>
    <div
      className={classNames(styles.fields, {
        [styles.hide]: frequency !== 'Custom',
      })}
    >
      <Field
        className={styles.field}
        name="quantity"
        type="text"
        margin="none"
        required
      />
      <Field
        className={styles.field}
        name="unit"
        type="select"
        options={UNITS}
        margin="none"
        required
      />
    </div>
  </>
);

export default VerificationFrequencyFields;
